import { useMemo } from 'react';
import type { ReactNode, VoidFunctionComponent } from 'react';

import Locator from '../../../../shared/Locator';
import Flex from '../../generic/Flex';
import Tabs, { useTabs } from '../../generic/Tabs';
import type { ReadDependencyLicenseAPIResponse } from '../../../../shared/types/licenses';
import type { LicenseResponse } from '../../../hooks/api/licenses/useLicense';

import CopyrightTab from './content/CopyrightTab';
import LicenseTextTab from './content/DependencyLicenseTextTab';
import GlobalLicenseTextTab from './content/GlobalLicenseTextTab';
import MatchesTab from './content/MatchesTab/MatchesTab';
import ObligationTab from './content/ObligationTab';
import SummaryTab from './content/SummaryTab';
import LicenseContentSkeleton from './LicenseContentSkeleton';
import type { DependencyProp, GroupedTerms } from './types';

import styles from './LicenseDrawer.module.less';

type GlobalScopeProps = {
  license?: LicenseResponse;
  isLoading: boolean;
  dependency?: never;
  revisionLicenseId?: never;
  buttons?: ReactNode[];
};

type DependencyScopeProps = {
  revisionLicenseId: number;
  license?: ReadDependencyLicenseAPIResponse;
  dependency: DependencyProp;
  isLoading: boolean;
  buttons?: ReactNode[];
};

type ContentProps = GlobalScopeProps | DependencyScopeProps;

const depScopeOnlyTabs = ['File Matches', 'Copyrights'];

const dependencyScopeTabList = ['Summary', 'License Text', 'File Matches', 'Copyrights', 'Obligations'];
const globalScopeTabList = dependencyScopeTabList.filter((tab) => !depScopeOnlyTabs.includes(tab));

const getDefaultGroupedTerms = (): GroupedTerms => ({
  CAN: [],
  CANNOT: [],
  MUST: [],
});

const LicenseContent: VoidFunctionComponent<ContentProps> = ({
  license,
  dependency,
  isLoading,
  revisionLicenseId,
  buttons,
}) => {
  const isDependencyScope = !!dependency;
  const tabs = useTabs({
    tabIds: isDependencyScope ? dependencyScopeTabList : globalScopeTabList,
    defaultTab: 'Summary',
  });

  const groupedTerms: GroupedTerms = useMemo(() => {
    if (!license?.Summary?.Terms) {
      return getDefaultGroupedTerms();
    }

    return license.Summary.Terms.reduce<GroupedTerms>((acc, term) => {
      acc[term.role].push(term);
      return acc;
    }, getDefaultGroupedTerms());
    // We use the licenseId to determine if the terms have changed because the terms are always the same
    // for a given licenseId, so it's cheaper to memoize the ID than the terms object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [license?.licenseId]);

  if (isLoading || !license) {
    return <LicenseContentSkeleton isDependencyScope={isDependencyScope} />;
  }

  return (
    <div className={styles.licenseContent} key={license.licenseId}>
      <div className={styles.title}>
        {license.name}
        {dependency && (
          <div className={styles.subtitle}>
            in <span className={styles.dependencyName}>{dependency.title}</span>
            <span className={styles.dependencyVersion}>
              {dependency.version ?? Locator.parse(dependency.locator).revision}
            </span>
          </div>
        )}
      </div>

      {buttons && (
        <Flex gap={8} justify="flex-start">
          {buttons.map((button) => (
            button
          ))}
        </Flex>
      )}

      <div className={styles.licenseContentContainer}>
        <Tabs>
          {tabs.tabs.map((tab) => (
            <Tabs.Tab
              key={tab.label}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...tab}
            />
          ))}
        </Tabs>
        <div className={styles.tabContent}>
          {tabs.activeTab === 'Summary' && (
            <SummaryTab
              summaryText={license?.Summary?.summary || 'No license summary available.'}
              groupedTerms={groupedTerms}
            />
          )}
          {tabs.activeTab === 'License Text' &&
            (isDependencyScope ? (
              <LicenseTextTab
                licenseText={license.licenseText || 'No license text available.'}
                revisionLicenseId={revisionLicenseId ?? undefined}
                dependency={dependency ?? undefined}
              />
            ) : (
              <GlobalLicenseTextTab licenseText={license.licenseText || 'No license text available.'} />
            ))}
          {tabs.activeTab === 'Obligations' && <ObligationTab groupedTerms={groupedTerms} />}
          {tabs.activeTab === 'Copyrights' && isDependencyScope && (
            <CopyrightTab
              copyright={license.copyright || null}
              revisionLicenseId={revisionLicenseId ?? undefined}
              dependency={dependency}
            />
          )}
          {dependency && tabs.activeTab === 'File Matches' && (
            <MatchesTab revisionId={dependency.locator} licenseId={license.licenseId} />
          )}
        </div>
      </div>
    </div>
  );
};

export default LicenseContent;
